import React from 'react';

import { useItineraryState, useToastState, useTripsState } from '#states/hooks.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ToastAction,
} from '#ui/index.js';

/**
 * DeleteItineraryItemDialog - Dialog for confirming deletion of an itinerary item
 */
export function DeleteItineraryItemDialog(): React.ReactNode {
  const itineraryState = useItineraryState();
  const toastState = useToastState();
  const tripsState = useTripsState();

  async function confirmDelete() {
    if (!itineraryState.itemToDelete || !tripsState.current?.id) return;

    const item = itineraryState.itemToDelete;
    if (!item.id) return;

    const itemTitle = item.title;
    const tripId = tripsState.current.id;
    const itemId = item.id;

    itineraryState.closeDeleteDialog(); // Close modal immediately

    // Show "deleting" toast immediately
    const deletingToastId = toastState.addToast({
      title: 'Removing item...',
      description: `${itemTitle} is being removed from your itinerary.`,
    });

    try {
      await itineraryState.deleteItem(itemId, tripId);

      // Remove the "deleting" toast and show success toast with undo action
      toastState.removeToast(deletingToastId);
      toastState.addToast({
        title: 'Item removed',
        variant: 'success',
        description: `${itemTitle} has been removed from your itinerary.`,
        action: (
          <ToastAction
            altText="Restore item"
            onClick={async () => {
              try {
                await itineraryState.restoreItem(itemId, tripId);
                toastState.addToast({
                  title: 'Item restored',
                  variant: 'success',
                  description: `${itemTitle} has been restored to your itinerary.`,
                });
              } catch (error) {
                console.error('Failed to restore itinerary item:', error);
                toastState.addToast({
                  title: 'Failed to restore item',
                  description: 'Please try again',
                  variant: 'destructive',
                });
              }
            }}
          >
            Undo
          </ToastAction>
        ),
      });
    } catch (error) {
      console.error('Failed to remove itinerary item:', error);

      // Remove the "deleting" toast and show error toast
      toastState.removeToast(deletingToastId);
      toastState.addToast({ title: 'Failed to remove item', description: 'Please try again.', variant: 'destructive' });
    }
  }

  return (
    <Dialog
      open={itineraryState.isDeleteDialogOpen}
      onOpenChange={(open) => !open && itineraryState.closeDeleteDialog()}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove {itineraryState.itemToDelete?.title} from itinerary?</DialogTitle>
          <DialogDescription>This item will be permanently removed from your trip itinerary.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => itineraryState.closeDeleteDialog()}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={confirmDelete}>
            Remove
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
